import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardTitle, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, Clock, Users, CheckCircle, TrendingUp, UserPlus, Stethoscope, ArrowRight, ChevronRight, CreditCard, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { NuovoAppuntamentoCard } from '@/components/backoffice/NuovoAppuntamentoCard';
import { PageTitle } from '@/components/backoffice/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  pending: { label: 'In Attesa', className: 'bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))]' },
  confirmed: { label: 'Confermato', className: 'bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))]' },
  rejected: { label: 'Rifiutato', className: 'bg-[hsl(var(--destructive))] text-[hsl(var(--destructive-foreground))]' },
  cancelled: { label: 'Annullato', className: 'bg-slate-500 text-white' },
  completed: { label: 'Completato', className: 'bg-[hsl(var(--info))] text-[hsl(var(--info-foreground))]' },
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paySummary, setPaySummary] = useState(null);
  const [paySearch, setPaySearch] = useState('');

  const loadStats = () => {
    axios.get(`${API}/dashboard/stats`)
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    axios.get(`${API}/payments/summary`)
      .then(r => setPaySummary(r.data))
      .catch(() => {});
  };

  useEffect(() => { loadStats(); }, []);

  const fmtEur = (v) => `${Number(v || 0).toFixed(2).replace('.', ',')} EUR`;
  const filteredPayments = (paySummary?.payments || []).filter(p => {
    if (!paySearch.trim()) return true;
    const q = paySearch.trim().toLowerCase();
    return `${p.patient_nome} ${p.patient_cognome}`.toLowerCase().includes(q) || (p.patient_email || '').toLowerCase().includes(q);
  });
  const filteredTotal = filteredPayments.reduce((s, p) => s + (p.amount || 0), 0);

  const kpis = stats ? [
    { label: 'Appuntamenti Oggi', value: stats.oggi, icon: Calendar, color: 'text-[#238A92] bg-[#238A92]/10', to: '/backoffice/appuntamenti?view=studios', hint: 'Vista giornaliera per studio' },
    { label: 'In Attesa', value: stats.in_attesa, icon: Clock, color: 'text-amber-600 bg-amber-50', to: '/backoffice/appuntamenti?status=pending', hint: 'Appuntamenti da confermare' },
    { label: 'Confermati', value: stats.confermati, icon: CheckCircle, color: 'text-green-600 bg-green-50', to: '/backoffice/appuntamenti?status=confirmed', hint: 'Appuntamenti confermati' },
    { label: 'Completati', value: stats.completati, icon: TrendingUp, color: 'text-blue-600 bg-blue-50', to: '/backoffice/appuntamenti?status=completed', hint: 'Appuntamenti completati' },
    { label: 'Pazienti Totali', value: stats.pazienti_totali, icon: Users, color: 'text-purple-600 bg-purple-50', to: '/backoffice/pazienti', hint: 'Anagrafica pazienti' },
    { label: 'Prenotazioni Ospite', value: stats.prenotazioni_ospite, icon: UserPlus, color: 'text-cyan-600 bg-cyan-50', to: '/backoffice/appuntamenti', hint: 'Tutti gli appuntamenti' },
  ] : [];

  return (
    <div>
      <div className="mb-6">
        <PageTitle title="Dashboard" info="Panoramica dello studio: clicca su un riquadro per aprire la sezione corrispondente." />
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {kpis.map(kpi => {
              const Icon = kpi.icon;
              return (
                <Link key={kpi.label} to={kpi.to} title={kpi.hint} data-testid={`kpi-link-${kpi.label}`} className="group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#238A92] rounded-xl">
                  <Card className="cursor-pointer transition-shadow duration-200 hover:shadow-md group-hover:border-[#238A92]/40 h-full">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-muted-foreground">{kpi.label}</span>
                        <div className={`w-10 h-10 rounded-lg ${kpi.color} flex items-center justify-center`}>
                          <Icon size={20} />
                        </div>
                      </div>
                      <div className="flex items-end justify-between">
                        <p className="text-3xl font-bold text-[#1F2A37]" style={{fontFamily:'Space Grotesk'}} data-testid={`kpi-${kpi.label}`}>{kpi.value}</p>
                        <span className="text-xs text-[#238A92] opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center gap-0.5 mb-1">Apri <ChevronRight size={12} /></span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>

          {/* Inserimento manuale appuntamento */}
          <div className="mb-6">
            <NuovoAppuntamentoCard onCreated={loadStats} />
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            {/* Weekly summary */}
            <Link to="/backoffice/appuntamenti?view=calendar" title="Apri il calendario settimanale" data-testid="week-card-link" className="group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#238A92] rounded-xl">
              <Card className="cursor-pointer transition-shadow duration-200 hover:shadow-md group-hover:border-[#238A92]/40 h-full">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg" style={{fontFamily:'Space Grotesk'}}>Questa Settimana</CardTitle>
                    <span className="text-xs text-[#238A92] opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center gap-0.5">Calendario <ChevronRight size={12} /></span>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold text-[#238A92] mb-1" style={{fontFamily:'Space Grotesk'}}>{stats?.settimana || 0}</p>
                  <p className="text-sm text-muted-foreground">Appuntamenti totali</p>
                </CardContent>
              </Card>
            </Link>

            {/* Pending per doctor */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg" style={{fontFamily:'Space Grotesk'}}>In Attesa per Dottore</CardTitle>
                  <Link to="/backoffice/appuntamenti?status=pending">
                    <Button variant="ghost" size="sm" className="text-[#238A92] h-7 px-2 text-xs">Vedi tutti <ArrowRight size={12} className="ml-1" /></Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                {stats?.in_attesa_per_dottore?.length > 0 ? (
                  <div className="space-y-1">
                    {stats.in_attesa_per_dottore.map((item) => (
                      <Link
                        key={`doc-${item.doctor_id || item.doctor}`}
                        to={`/backoffice/appuntamenti?status=pending${item.doctor_id ? `&doctor=${item.doctor_id}` : ''}`}
                        title={`Apri gli appuntamenti in attesa del Dott. ${item.doctor}`}
                        data-testid={`pending-doctor-link-${item.doctor}`}
                        className="flex items-center justify-between rounded-md px-2 py-1.5 -mx-2 hover:bg-[hsl(var(--secondary))] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#238A92]"
                      >
                        <div className="flex items-center gap-2">
                          <Stethoscope size={14} className="text-muted-foreground" />
                          <span className="text-sm">Dott. {item.doctor}</span>
                        </div>
                        <span className="flex items-center gap-1.5">
                          <Badge className="bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))]">{item.count}</Badge>
                          <ChevronRight size={14} className="text-muted-foreground" />
                        </span>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Nessun appuntamento in attesa</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Pagamenti Ricevuti (Stripe) */}
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Space Grotesk'}}><CreditCard size={18} /> Pagamenti Ricevuti</CardTitle>
                <div className="relative w-full sm:w-64">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={paySearch}
                    onChange={e => setPaySearch(e.target.value)}
                    placeholder="Cerca paziente..."
                    className="pl-8 h-9"
                    data-testid="payments-search-input"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Totali */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                {[
                  { label: 'Oggi', key: 'oggi' },
                  { label: 'Questa Settimana', key: 'settimana' },
                  { label: 'Questo Mese', key: 'mese' },
                ].map(t => (
                  <div key={t.key} className="rounded-lg border bg-[hsl(var(--secondary))] p-4" data-testid={`payments-total-${t.key}`}>
                    <p className="text-xs text-muted-foreground mb-1">{t.label}</p>
                    <p className="text-2xl font-bold text-[#1E7A58]" style={{fontFamily:'Space Grotesk'}}>{fmtEur(paySummary?.totals?.[t.key])}</p>
                    <p className="text-xs text-muted-foreground">{paySummary?.counts?.[t.key] || 0} {paySummary?.counts?.[t.key] === 1 ? 'pagamento' : 'pagamenti'}</p>
                  </div>
                ))}
              </div>
              {/* Elenco pagamenti */}
              {filteredPayments.length > 0 ? (
                <>
                  {paySearch.trim() && (
                    <p className="text-sm mb-2" data-testid="payments-filtered-total">
                      <span className="text-muted-foreground">Totale per la ricerca "{paySearch.trim()}":</span> <strong className="text-[#1E7A58]">{fmtEur(filteredTotal)}</strong> ({filteredPayments.length} {filteredPayments.length === 1 ? 'pagamento' : 'pagamenti'})
                    </p>
                  )}
                  <div className="border rounded-lg overflow-x-auto max-h-80 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Paziente</TableHead>
                          <TableHead>Servizio</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Visita</TableHead>
                          <TableHead>Pagato il</TableHead>
                          <TableHead className="text-right">Importo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredPayments.map(p => (
                          <TableRow key={p.id} data-testid="payment-row">
                            <TableCell className="font-medium">
                              <Link to={`/backoffice/appuntamenti?appt=${p.appointment_id}`} className="hover:text-[#238A92] hover:underline" title="Apri il dettaglio dell'appuntamento">
                                {p.patient_nome} {p.patient_cognome}
                              </Link>
                            </TableCell>
                            <TableCell className="text-sm">{p.service_nome}{p.doctor_cognome ? ` — Dott. ${p.doctor_cognome}` : ''}</TableCell>
                            <TableCell><Badge variant="secondary" className="text-[10px]">{p.payment_type === 'deposit' ? 'Acconto' : 'Saldo'}</Badge></TableCell>
                            <TableCell className="text-sm">{p.data_appuntamento}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{p.paid_at ? new Date(p.paid_at).toLocaleString('it-IT', {dateStyle:'short', timeStyle:'short'}) : ''}</TableCell>
                            <TableCell className="text-right font-semibold text-[#1E7A58]">{fmtEur(p.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground py-4 text-center" data-testid="payments-empty">
                  {paySearch.trim() ? `Nessun pagamento trovato per "${paySearch.trim()}"` : 'Nessun pagamento online ricevuto finora'}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Upcoming appointments */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg" style={{fontFamily:'Space Grotesk'}}>Prossimi Appuntamenti</CardTitle>
                <Link to="/backoffice/appuntamenti">
                  <Button variant="ghost" size="sm" className="text-[#238A92]">Vedi Tutti <ArrowRight size={14} className="ml-1" /></Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {stats?.prossimi_appuntamenti?.length > 0 ? (
                <div className="space-y-2">
                  {stats.prossimi_appuntamenti.map((a, i) => (
                    <Link
                      key={a.id || `appt-${i}`}
                      to={`/backoffice/appuntamenti?appt=${a.id}`}
                      title="Apri il dettaglio dell'appuntamento"
                      data-testid={`upcoming-appt-link-${a.id}`}
                      className="flex items-center justify-between border rounded-lg p-3 hover:bg-[hsl(var(--secondary))] hover:border-[#238A92]/40 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#238A92]"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{a.patient_nome} {a.patient_cognome}</span>
                          {a.source === 'guest' && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Ospite</Badge>}
                          {a.source === 'whatsapp' && <Badge className="text-[10px] px-1.5 py-0 bg-[#238A92]/15 text-[#0F5E66] hover:bg-[#238A92]/15">WhatsApp</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground">{a.service_nome} - Dott. {a.doctor_cognome}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">{a.data}</p>
                        <p className="text-xs text-muted-foreground">{a.ora}</p>
                      </div>
                      <Badge className={`ml-3 ${STATUS_MAP[a.status]?.className}`}>{STATUS_MAP[a.status]?.label}</Badge>
                      <ChevronRight size={16} className="ml-2 text-muted-foreground flex-shrink-0" />
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4 text-center">Nessun appuntamento in programma</p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
